'use strict';

const MINIMUM_BUFFER_AHEAD_SECONDS = 0.25;

const getLogSafePath = url => {
  if (typeof url !== 'string') return url;
  if (url.startsWith('data:')) {
    return `${url.slice(0, 64)}...(${url.length} chars)`;
  }
  if (url.startsWith('blob:')) return url;
  try {
    const parsedUrl = new URL(url);
    return `${parsedUrl.origin}${parsedUrl.pathname}`;
  } catch (_) {
    return url;
  }
};

const getPrimarySourceType = source => {
  if (source?.src?.hls) return 'hls';
  if (source?.src?.dash) return 'dash';
  if (source?.src?.mp4) return 'mp4';
  return null;
};

const roundMediaSeconds = value =>
  Number.isFinite(value) ? Math.round(value * 1000) / 1000 : value;

const getVideoBufferedSummary = videoElement => {
  const { buffered } = videoElement || {};
  const videoCurrentTimeSeconds = Number.isFinite(videoElement?.currentTime)
    ? videoElement.currentTime
    : 0;
  const summary = {
    videoBufferedRangesSeconds: [],
    videoBufferAheadSeconds: 0,
    isVideoCurrentTimeBuffered: false,
    videoNextBufferedRangeStartSeconds: null,
  };
  if (!buffered?.length) return summary;

  for (let index = 0; index < buffered.length; index++) {
    const startSeconds = buffered.start(index);
    const endSeconds = buffered.end(index);

    summary.videoBufferedRangesSeconds.push({
      startSeconds: roundMediaSeconds(startSeconds),
      endSeconds: roundMediaSeconds(endSeconds),
    });

    if (
      videoCurrentTimeSeconds >= startSeconds &&
      videoCurrentTimeSeconds <= endSeconds
    ) {
      summary.isVideoCurrentTimeBuffered = true;
      summary.videoBufferAheadSeconds = roundMediaSeconds(
        Math.max(endSeconds - videoCurrentTimeSeconds, 0)
      );
      continue;
    }

    if (
      videoCurrentTimeSeconds < startSeconds &&
      summary.videoNextBufferedRangeStartSeconds === null
    ) {
      summary.videoNextBufferedRangeStartSeconds =
        roundMediaSeconds(startSeconds);
    }
  }
  return summary;
};

/** Create a fireCallback bound to a log function. */
export const createFireCallback = log => (callback, label, payload) => {
  if (typeof callback !== 'function') return;
  try {
    payload === undefined ? callback() : callback(payload);
  } catch (error) {
    log(`${label}:threw`, { error: error?.message || String(error) });
  }
};

/** Build readable video element fields for player debug logs. */
export const getVideoPlaybackLogData = videoElement => ({
  videoCurrentTimeSeconds: roundMediaSeconds(videoElement?.currentTime ?? 0),
  videoDurationSeconds: roundMediaSeconds(videoElement?.duration ?? 0),
  ...getVideoBufferedSummary(videoElement),
  videoReadyState: videoElement?.readyState,
  videoNetworkState: videoElement?.networkState,
  videoPaused: videoElement?.paused,
  videoSeeking: videoElement?.seeking,
  videoEnded: videoElement?.ended,
  videoWidth: videoElement?.videoWidth,
  videoHeight: videoElement?.videoHeight,
});

const getWaitingFor = (videoElement, playbackLogData) => {
  if (!videoElement) {
    return 'Waiting for the video element to be available before playback can continue.';
  }
  if (playbackLogData.videoSeeking) {
    return 'Waiting for media data at the seek target before playback can continue.';
  }
  if (playbackLogData.videoReadyState <= 1) {
    // HAVE_METADATA
    return 'Waiting for enough media data to be loaded before playback can continue.';
  }
  if (!playbackLogData.isVideoCurrentTimeBuffered) {
    if (playbackLogData.videoNextBufferedRangeStartSeconds !== null) {
      return 'Waiting for buffered data to cover the current playback time; the next buffered range starts later.';
    }
    return 'Waiting for buffered data at the current playback time.';
  }
  if (playbackLogData.videoBufferAheadSeconds <= MINIMUM_BUFFER_AHEAD_SECONDS) {
    return 'Waiting for more buffer ahead of the current playback time.';
  }
  if (!playbackLogData.videoWidth || !playbackLogData.videoHeight) {
    return 'Waiting for the first video frame to render with a valid size.';
  }
  if (playbackLogData.videoPaused) {
    return 'Waiting for playback to resume from the paused state.';
  }
  return 'Waiting for playback progress after the player reported a stall.';
};

/** Build readable waiting fields for player debug logs. */
export const getVideoWaitingLogData = videoElement => {
  const playbackLogData = getVideoPlaybackLogData(videoElement);
  return {
    waitingFor: getWaitingFor(videoElement, playbackLogData),
    ...playbackLogData,
  };
};

/** Extract loggable fields from an adapter event payload. */
export const getAdapterEventLogData = data => {
  if (data === undefined || data === null) return undefined;
  if (typeof data !== 'object') return { value: data };

  return {
    code: data.code,
    name: data.name,
    type: data.type,
    reason: data.reason,
    message: data.message,
    source: data.source,
    time: data.time,
  };
};

/** Build readable source fields for player debug logs. */
export const getSourceLogData = source => {
  const sourceType = getPrimarySourceType(source);
  const sourceUrl = sourceType ? source?.src?.[sourceType] : undefined;
  const isSourceBlobUri =
    typeof sourceUrl === 'string' && sourceUrl.includes('blob:');
  const isSourceDataUrlHls =
    typeof sourceUrl === 'string' &&
    sourceUrl.startsWith('data:application/vnd.apple.mpegurl');

  return {
    messageId: source?.id,
    sourceType,
    sourceUrl,
    sourcePathname: getLogSafePath(sourceUrl),
    isSourceUndefined: sourceUrl === undefined,
    isSourceBlobUri,
    isSourceDataUrlHls,
    mimeHint: isSourceBlobUri
      ? 'video/mp4'
      : isSourceDataUrlHls
        ? 'application/vnd.apple.mpegurl'
        : {
            dash: 'application/dash+xml',
            hls: 'application/vnd.apple.mpegurl',
            mp4: 'video/mp4',
          }[sourceType],
    hasDrm: !!source?.drm,
    drmKeySystem: source?.drm?.keySystem,
  };
};

/**
 * Build playback log prefix: `[msg:xxx][player:yyy]`.
 * @param {object} params
 * @param {string} [params.messageId] - Message identifier.
 * @param {string} [params.playerId] - Player internal identifier.
 * @returns {string} Formatted prefix string.
 * @example
 * getPlaybackLogPrefix({ messageId: 'abc', playerId: 'p-1' });
 * // => '[msg:abc][player:p-1]'
 */
export const getPlaybackLogPrefix = ({ messageId, playerId } = {}) => {
  let prefix = '';
  if (messageId) prefix += `[msg:${messageId}]`;
  if (playerId) prefix += `[player:${playerId}]`;
  return prefix;
};

/**
 * Build structured payload for failed playback events.
 * @param {object} params
 * @param {string} params.stage - Failure stage (e.g. 'load', 'play').
 * @param {Error} params.error - The error object.
 * @param {string} [params.messageId] - Message identifier.
 * @param {string} [params.playerId] - Player internal identifier.
 * @param {string} [params.lifecycle] - Current lifecycle state.
 * @param {string} [params.loadMode] - Current load mode.
 * @param {string} [params.sourceType] - Source type (dash/hls/mp4).
 * @param {HTMLVideoElement} [params.videoElement] - Video element for diagnostics.
 * @param {number} [params.retryCount] - Number of retries attempted.
 * @returns {object} Structured log payload.
 * @example
 * getPlaybackFailedLogData({ stage: 'load', error: new Error('timeout') });
 * // => { stage: 'load', errorMessage: 'timeout', ... }
 */
export const getPlaybackFailedLogData = ({
  stage,
  error,
  messageId,
  playerId,
  lifecycle,
  loadMode,
  sourceType,
  videoElement,
  retryCount = 0,
}) => ({
  messageId,
  playerId,
  stage,
  errorName: error?.name,
  errorCode: error?.code,
  errorMessage: error?.message || String(error),
  lifecycle,
  loadMode,
  sourceType,
  videoReadyState: videoElement?.readyState,
  videoNetworkState: videoElement?.networkState,
  videoCurrentTimeSeconds: Number.isFinite(videoElement?.currentTime)
    ? Math.round(videoElement.currentTime * 1000) / 1000
    : undefined,
  retryCount,
});
